import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";

import reportWebVitals from "./reportWebVitals";
import * as Sentry from "@sentry/react";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import posthog from "posthog-js";

import "./index.css";
import useAuth from "./hooks/useAuth";
import { createOrganization } from "./ctapi/organizations";
import { App } from "./components";
import CreateOrganizationDialog from "./components/CreateOrganizationDialog/CreateOrganizationDialog";
import { OrganizationProvider } from "./shared/OrganizationContext";
import PotentialCandidateForm from "./components/PotentialCandidateForm";
import {
  createMatchPublic,
  getIdealCandidatePublic,
} from "./ctapi/idealCandidates";
import config from "./config";
import { FirebaseAuthUi } from "./firebase/FirebaseAuthUi";
import { TourProvider } from './shared/TourContext';
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material"; 
import LegalPages from "./components/App/Legal/LegalPages"; 
import SignInPage from "./components/Authentication/SignInForm/SignInPage";
import SignUpPage from './components/Authentication/SignUpForm/SignUpPage'; 

if (config.posthog) {
  posthog.init(config.posthog.key, { api_host: config.posthog.host });
}

if (config.sentryEnabled) {
  Sentry.init({
    dsn: "https://78763da35da5113b5a9af8c9e1fc6378@o4505607970357248.ingest.sentry.io/4505863887978496",
    release: process.env.REACT_APP_RELEASE,
    environment: process.env.REACT_APP_ENV,
    replaysOnErrorSampleRate: 1.0,
    integrations: [new Sentry.Replay()],
    // eslint-disable-next-line no-unused-vars
    beforeSend(event, hint) {
      if (event.exception) {
        for (let exception of event.exception.values) {
          if (
            exception.type === "Error" &&
            exception.value.includes("ResizeObserver loop")
          ) {
            return null;
          }
        }
      }
      return event;
    },
  });
}

const AppRootComponent = () => {
  const { user, signOut, isLoading } = useAuth();
  const [organization, setOrganization] = useState(null);

  useEffect(() => {
    if (user) {
      setOrganization(user.defaultOrganization);
    }
  }, [user]);

  const handleCreateOrganization = async (organizationDialogResult) => {
    const createOrganizationResponse = await createOrganization(
      organizationDialogResult
    );
    setOrganization(createOrganizationResponse);
  };

  if (isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
    
  return (
    <React.StrictMode>
      <Sentry.ErrorBoundary>
        <FirebaseAuthUi>
          {user && !organization ? (
            <CreateOrganizationDialog
              open={true}
              onCreate={handleCreateOrganization}
            />
          ) : null}
          {user && organization ? (
            <OrganizationProvider value={organization}>
              <TourProvider>
                <App user={user} signOut={signOut} />
              </TourProvider>
            </OrganizationProvider>
          ) : null}
        </FirebaseAuthUi>
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );
};

// TODO: This should really be served as its own app,
//       but to reduce complexity, we're going to just
//       serve it in the same Amplify App initially.
const isApp = () => {
  const path = window.location.pathname;
  return !path.startsWith("/apply");
};

// TODO: Move majority of this component into a PotentialCandidateFormContainer.
const transformIdealCandidate = (ic) => {
  return {
    id: ic.uuid,
    title: ic.title,
    theme: ic.theme,
    organizationName: ic.organizationName,
    organizationDescriptiveName: ic.organizationDescriptiveName,
    skills: ic.scorecards
      .find((scorecard) => scorecard.name === "skills")
      .criteria.map((skill) => {
        return {
          id: skill.id,
          name: skill.label,
          description: skill.description,
          weight: skill.weight,
          levels: skill.levels,
        };
      }),
    behaviors: ic.scorecards
      .find((scorecard) => scorecard.name === "behaviors")
      .criteria.map((behavior) => {
        return {
          id: behavior.id,
          name: behavior.label,
          description: behavior.description,
          weight: behavior.weight,
          levels: behavior.levels,
        };
      }),
  };
};

const PotentialCandidateRootComponent = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const [idealCandidate, setIdealCandidate] = useState(null);
  const idealCandidateUuid = queryParams.get("ic");

  const onSubmit = async (results) => {
    await createMatchPublic(idealCandidate, results);
  };

  useEffect(() => {
    const fetchIdealCandidate = async () => {
      if (!idealCandidate && idealCandidateUuid) {
        const retrievedIdealCandidate = await getIdealCandidatePublic(
          idealCandidateUuid
        );
        setIdealCandidate(transformIdealCandidate(retrievedIdealCandidate));
      }
    };
    fetchIdealCandidate();
  }, [idealCandidateUuid, idealCandidate]);
  if (idealCandidate) {
    return (
      <Sentry.ErrorBoundary>
        <PotentialCandidateForm
          idealCandidate={idealCandidate}
          onSubmit={onSubmit}
        />
        );
      </Sentry.ErrorBoundary>
    );
  } else {
    return <div>Loading...</div>;
  }
};

const AppRouter = () => {
  // useAuth now drives the routing decisions at the top level
  const { user, isLoading } = useAuth();

  // Show a global loader while we check for an active session
  if (isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
    
  return (
    <BrowserRouter>
      <Routes>
        {user ? (
          // AUTHENTICATED ROUTES
          <>
            {/* If a logged-in user tries to visit signin/signup, redirect them to the dashboard */}
            <Route path="/signin" element={<Navigate to="/" replace />} />
            <Route path="/signup" element={<Navigate to="/" replace />} />
            
            {/* All other paths under the authenticated experience are handled by AppRootComponent */}
            <Route path="/*" element={<AppRootComponent />} />
          </>
        ) : (
          // PUBLIC ROUTES
          <>
            <Route path="/apply" element={<PotentialCandidateRootComponent />} />
            <Route path="/terms/*" element={<LegalPages />} />
            <Route path="/privacy/*" element={<LegalPages />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/" element={<SignInPage />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </BrowserRouter>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(<AppRouter />);

serviceWorkerRegistration.register();
reportWebVitals();
